import {
  ArrowRightIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';
import { useMobile } from 'common-nextjs';
import { Button } from 'ui';
import { displayPrice } from 'utils';
import Image from '~/components/Image';
import AddToCart from '~/components/ItemDetails/AddToCart';
import BuyerProtection from '~/components/ItemDetails/BuyerProtection';
import ItemFavoriteButton from '~/components/ItemDetails/ItemFavoriteButton';
import ItemValueProps from '~/components/ItemDetails/ItemValueProps';
import ItemDetailsLink from '~/components/Links/ItemDetailsLink';
import usePrice from '~/hooks/usePrice';
import usePurchaseItem from '~/hooks/usePurchaseItem';
import { RailsItem } from '~/typings/services/rails/item';
import ManagedSwipeableCarouselContainer from '../Swipeable/ManagedSwipeableCarouselContainer';

function PriceInfo({ item }: { item: RailsItem }) {
  const calculatePrice = usePrice();
  const retail = calculatePrice(item.price_retail);

  return (
    <div className="flex items-center space-x-2">
      <div className="text-2xl font-semibold">{displayPrice(item.price)}</div>

      {item.price_retail && (
        <div className="text-slate-green-500 font-semibold line-through">
          Retail: {retail.formattedPrice} {retail.nonUSDCurrency}
        </div>
      )}

      {item.price_retail > item.price && (
        <span className="font-semibold text-red-500">
          (You save {displayPrice(item.price_retail - item.price, false)})
        </span>
      )}
    </div>
  );
}

function Actions({ item }: { item: RailsItem }) {
  const mobile = useMobile();
  const { onMessageSeller, onBuyNow, onMakeOffer } = usePurchaseItem(item);

  const availableAction =
    item.actions.find(action => action === 'add_to_cart') ||
    item.actions.find(action => action === 'buy') ||
    item.actions.find(action => action === 'offer');

  if (mobile) {
    const renderedAction = {
      buy: (
        <Button
          size="large"
          color="primary"
          onClick={e => onBuyNow(e)}
          variant="contained"
          className="flex-1"
        >
          Buy Now
        </Button>
      ),
      offer: (
        <Button
          size="large"
          color="primary"
          onClick={e => onMakeOffer(e)}
          variant="contained"
          className="flex-1"
        >
          Make Offer
        </Button>
      ),
      add_to_cart: <AddToCart primary item={item} className="flex-1" />,
    };

    return (
      <div>
        <div className="flex gap-2">
          {availableAction ? renderedAction[availableAction] : null}

          <ItemFavoriteButton item={item} showLabel={false} />
          <Button
            size="large"
            color="grey"
            onClick={() => onMessageSeller()}
            variant="outlined"
          >
            <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
          </Button>
        </div>

        <BuyerProtection className="mt-4" />
      </div>
    );
  } else {
    const renderedAction = {
      buy: (
        <Button
          size="large"
          color="primary"
          onClick={e => onBuyNow(e)}
          variant="contained"
          className="col-span-2"
        >
          Buy Now
        </Button>
      ),
      offer: (
        <Button
          size="large"
          color="primary"
          onClick={e => onMakeOffer(e)}
          variant="contained"
          className="col-span-2"
        >
          Make Offer
        </Button>
      ),
      add_to_cart: <AddToCart primary item={item} className="col-span-2" />,
    };
    return (
      <div className="grid grid-cols-2 gap-2">
        {availableAction ? renderedAction[availableAction] : null}

        <ItemFavoriteButton item={item} />
        <Button
          size="large"
          onClick={() => onMessageSeller()}
          variant="outlined"
          className="flex items-center gap-2"
        >
          <ChatBubbleLeftEllipsisIcon className="h-6 w-6" />
          <span>Message Seller</span>
        </Button>
      </div>
    );
  }
}

function MobilePlaHeader({ item }: { item: RailsItem }) {
  return (
    <>
      <ItemDetailsLink item={item} prefetch={false}>
        <h1 className="mb-4 line-clamp-2 text-base font-semibold">
          {item.name}
        </h1>
      </ItemDetailsLink>

      <ManagedSwipeableCarouselContainer
        className="mb-2"
        showIndicators
        indicatorColor="white"
        indicatorPosition="bottom-floating"
      >
        {item.images.map(image => (
          <div key={image.id} className="aspect-w-9 aspect-h-11">
            <Image
              src={image.edge_url}
              useFastly={{
                height: 300,
              }}
              className="rounded object-cover"
            />
          </div>
        ))}
      </ManagedSwipeableCarouselContainer>

      <div className="my-4">
        <PriceInfo item={item} />
      </div>

      <Actions item={item} />
    </>
  );
}

function DesktopPlaHeader({ item }: { item: RailsItem }) {
  return (
    <div className="grid gap-4 md:grid-cols-[250px_1fr] xl:grid-cols-[250px_1fr_400px]">
      <div className="row-span-2">
        <ManagedSwipeableCarouselContainer
          showIndicators
          indicatorColor="white"
          indicatorPosition="bottom-floating"
          showNextPrevButtons
        >
          {item.images.map(image => (
            <div key={image.id} className="aspect-w-10 aspect-h-11">
              <Image
                src={image.edge_url}
                useFastly={{
                  height: 300,
                }}
                height={250}
                className="rounded object-cover"
              />
            </div>
          ))}
        </ManagedSwipeableCarouselContainer>
      </div>

      <div>
        <ItemDetailsLink item={item} prefetch={false}>
          <h1 className="mb-4 line-clamp-2 text-2xl font-bold">{item.name}</h1>
        </ItemDetailsLink>

        <div className="my-4">
          <PriceInfo item={item} />
        </div>

        <ItemValueProps item={item} />
      </div>

      <div>
        <Actions item={item} />

        <ItemDetailsLink
          prefetch={false}
          item={item}
          className="flex items-center justify-center gap-2 py-8 text-lg font-semibold"
        >
          <span>See Full Details</span>
          <ArrowRightIcon className="h-6 w-6" />
        </ItemDetailsLink>
      </div>
    </div>
  );
}

function PlaHeader({ item }: { item: RailsItem }) {
  const mobile = useMobile();

  return (
    <section className="mb-4 rounded border px-4 py-3">
      {mobile ? (
        <MobilePlaHeader item={item} />
      ) : (
        <DesktopPlaHeader item={item} />
      )}
    </section>
  );
}

export default PlaHeader;
